import type { PlayerStanding } from "@/types/api";
import { Trophy, Medal, Award, BarChart3 } from "lucide-react";

interface StandingsProps {
  players: PlayerStanding[];
  myPlayerId: string;
}

const rankConfig = [
  { bg: "bg-warning/15", text: "text-warning", Icon: Trophy, iconSize: "h-6 w-6" },
  { bg: "bg-accent/15", text: "text-accent", Icon: Medal, iconSize: "h-5 w-5" },
  { bg: "bg-primary/15", text: "text-primary", Icon: Award, iconSize: "h-5 w-5" },
];

export default function Standings({ players, myPlayerId }: StandingsProps) {
  const sorted = [...players].sort(
    (a, b) => (b.totalScore ?? b.approvedCount) - (a.totalScore ?? a.approvedCount),
  );

  return (
    <div className="space-y-3">
      {sorted.map((p, i) => {
        const pct =
          p.totalItems > 0
            ? Math.round((p.approvedCount / p.totalItems) * 100)
            : 0;
        const isMe = p.playerId === myPlayerId;
        const rank = rankConfig[i];

        return (
          <div
            key={p.playerId}
            className={`animate-fade-in-up rounded-2xl border-2 p-4 transition-all ${
              isMe
                ? "border-primary/40 bg-primary/5"
                : "border-border bg-surface"
            }`}
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="flex items-center gap-3">
              {/* Rank badge */}
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
                rank ? `${rank.bg} ${rank.text}` : "bg-surface-sunken text-text-secondary"
              }`}>
                {rank ? (
                  <rank.Icon className={rank.iconSize} />
                ) : (
                  <span className="text-lg font-extrabold">{i + 1}</span>
                )}
              </span>

              {/* Name + progress */}
              <div className="min-w-0 flex-1">
                <p className="text-base font-bold text-text">
                  {p.displayName}
                  {isMe && (
                    <span className="ml-1.5 text-sm font-medium text-primary">
                      (you)
                    </span>
                  )}
                </p>
                <div className="mt-2 h-3 overflow-hidden rounded-full bg-surface-sunken">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-accent to-accent-hover transition-all duration-700 ease-out"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>

              {/* Score */}
              <div className="shrink-0 text-right">
                <span className="text-2xl font-extrabold tabular-nums text-text">
                  {p.totalScore ?? p.approvedCount}
                </span>
                <span className="text-sm font-medium text-text-secondary">
                  /{p.totalItems}
                </span>
                {(p.voteBonus ?? 0) > 0 && (
                  <p className="text-xs text-error">+{p.voteBonus} votes</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {sorted.length === 0 && (
        <div className="flex flex-col items-center py-12 text-text-secondary">
          <BarChart3 className="mb-3 h-10 w-10 opacity-40" />
          <p className="text-base font-medium">No standings yet</p>
        </div>
      )}
    </div>
  );
}
